import { ContentEnrollment } from '@/store/content/types';
import { setContentFavoriteRequest } from '@/store/favorite/actions';
import { Dispatch } from 'redux';
import Constants from '../Constants';
import { AvailableLanguage } from '../models/AvailableLanguage';
import { AvailableTranslation } from '../models/AvailableTranslation';
import { ContentMetadata } from '../models/ContentMetadata';
import Item from '../models/Item';
import { LanguageType } from '../models/LanguageType';
import { store } from '../store';
import { Favorite } from '../store/favorite/types';
import { LocaleData } from '../translations/LocaleData';
import { analyticsService, PlaylistEvents } from './AnalyticsService';

export class ContentService {
    getContentMetadata = (content: Item) => {
        // TODO - AJUSTAR PARA FUNCIONAR COM LIÇÃO DO TIPO SCORM
        let mType: string = content.mediaType || 'SCORM';

        if (content.dynamicAttributes && content.dynamicAttributes.type) {
            mType = content.dynamicAttributes.type;
        }
        return this.getMetaData(mType);
    };

    getContentTranslationByLanguageType = (content: Item, languageType: LanguageType): AvailableTranslation => {
        const availableTranslation =
            content &&
            content.availableTranslations &&
            content.availableTranslations.find((availableTranslation: AvailableTranslation) => availableTranslation.locale === languageType);

        if (availableTranslation) {
            return availableTranslation;
        }

        return this.getDefaultContentTranslation(content);
    };

    getDefaultContentTranslation = (content: Item): AvailableTranslation => {
        const availableTranslation =
            content &&
            content.availableTranslations &&
            content.availableTranslations.find((availableTranslation: AvailableTranslation) => availableTranslation.locale === content.defaultLocale);

        if (availableTranslation) {
            return availableTranslation;
        }
        return { name: content.name, author: content.author, workload: content.workload, description: content.description } as AvailableTranslation;
    };

    private getMetaData = (key: string): ContentMetadata => {
        const strings: LocaleData = store.getState().locale.data;
        const metadata: { [key: string]: ContentMetadata } = {
            AUDIO: {
                typeName: strings.AUDIO_TYPE_NAME,
                changeLanguageLabel: strings.AUDIO,
            },
            VIDEO: {
                typeName: strings.VIDEO_TYPE_NAME,
                changeLanguageLabel: strings.VIDEO,
            },
            DOCUMENT: {
                typeName: strings.DOCUMENT_TYPE_NAME,
                changeLanguageLabel: strings.DOCUMENT_CHANGE_LANGUAGE_LABEL,
            },
            HTML: {
                typeName: strings.DOCUMENT_TYPE_NAME,
                changeLanguageLabel: strings.DOCUMENT_CHANGE_LANGUAGE_LABEL,
            },
            EXTERNAL: {
                typeName: strings.EXTERNAL_LINK,
                changeLanguageLabel: strings.EXTERNAL_LINK,
            },
            SCORM: {
                typeName: 'SCORM',
                changeLanguageLabel: 'SCORM',
            },
        };

        return metadata[key];
    };

    isFavorited = (contentId: number, favoriteList: Favorite[]): boolean => {
        const resultList = favoriteList.filter(
            (item: Favorite) => item.contentType === Constants.entityTypes.CONTENT && item.contentId === contentId,
        );
        return resultList.length > 0;
    };

    getContentPathByLanguageType = (content: Item, languageType: LanguageType): string => {
        if (content.availableLanguages) {
            return (content.availableLanguages.find((availableLanguage: AvailableLanguage) => availableLanguage.locale === languageType) || content)
                .path;
        }
        return '';
    };

    getContentSubtitlePathByLanguageType = (content: Item, languageType: LanguageType): string | undefined => {
        return content?.availableSubtitles?.find((availableSubtitle: AvailableLanguage) => availableSubtitle.locale === languageType)?.path;
    };

    favoriteContent = (dispatch: Dispatch, content: Item) => {
        dispatch(
            setContentFavoriteRequest({
                contentId: content.id,
                contentType: Constants.entityTypes.CONTENT,
            } as Favorite),
        );

        analyticsService.logEvent(PlaylistEvents.FAVORITE_ITEM, {
            entityId: content.id,
            entityName: content.name,
            entityType: Constants.entityTypes.CONTENT,
        });
    };

    getContentStatusIsCompleted = (contentEnrollment: ContentEnrollment): boolean => {
        const hasContentEnrollment: boolean = !!contentEnrollment;
        return hasContentEnrollment && contentEnrollment.status === Constants.contentEnrollmentStatus.COMPLETED;
    };
}

export const contentService = new ContentService();
